import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Copy, Mail, MessageSquare, Plus, QrCode, Trash2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { INTAKE_MODEL_STATUS_LABELS, INTAKE_MODEL_STATUS_STYLES } from "@/components/org/intake/intakeMeta";
import { CT } from "@/lib/connectionsTheme";

export const IntakeModelsTab = ({ canManage, onModelsChanged }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [models, setModels] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/org/intake/models`, { withCredentials: true });
      setModels(data.models);
      setSelectedId((prev) => (prev && data.models.some((m) => m.model_id === prev) ? prev : data.models[0]?.model_id || null));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (modelId, status) => {
    const warning = status === "draft"
      ? "Delete this draft Intake Model? This can't be undone."
      : "Delete this Intake Model? Past submissions stay in Front Desk, but this configuration and its activation history will be permanently removed. This can't be undone.";
    if (!window.confirm(warning)) return;
    setBusyId(modelId);
    setError("");
    try {
      await axios.delete(`${API}/org/intake/models/${modelId}`, { withCredentials: true });
      load();
      onModelsChanged?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const handleDuplicate = async (modelId) => {
    setBusyId(modelId);
    setError("");
    try {
      await axios.post(`${API}/org/intake/models/${modelId}/duplicate`, {}, { withCredentials: true });
      load();
      onModelsChanged?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const ctaLabel = (status) => (status === "draft" ? "Continue Setup" : "Manage");
  const selected = models?.find((m) => m.model_id === selectedId) || null;

  return (
    <div className="space-y-4" data-testid="intake-models-tab">
      <div className="flex items-center justify-between">
        <p className={CT.textSecondary}>Define what information customers provide, and how they verify their identity, before a request reaches Front Desk.</p>
        {canManage && (
          <button type="button" onClick={() => navigate("/dashboard/connections/customer-intake/models/new")} data-testid="intake-models-create-btn" className={`${CT.btnPrimary} shrink-0`}>
            <Plus size={14} /> Create Intake Model
          </button>
        )}
      </div>

      {error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400" data-testid="intake-models-error">{error}</p>}

      {models === null && <p className="text-sm text-slate-500" data-testid="intake-models-loading">Loading…</p>}

      {models && models.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500 dark:border-white/15 dark:text-slate-400" data-testid="intake-models-empty">
          No Intake Models yet. {canManage ? "Create one to get started." : "Ask an Owner/Admin to create one."}
        </div>
      )}

      {models && models.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_1fr]" data-testid="intake-models-list">
          <div className="space-y-2">
            {models.map((m) => (
              <button
                key={m.model_id}
                type="button"
                onClick={() => setSelectedId(m.model_id)}
                data-testid={`intake-model-select-${m.model_id}`}
                className={`block w-full rounded-xl border p-3.5 text-left transition-colors duration-150 ${
                  selectedId === m.model_id ? "border-blue-400 bg-blue-50 dark:border-blue-500/50 dark:bg-blue-500/10" : "border-slate-200 bg-white hover:border-blue-300 dark:border-white/10 dark:bg-[#0F172A] dark:hover:border-blue-500/30"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-bold text-slate-900 dark:text-white" data-testid={`intake-model-name-${m.model_id}`}>{m.name}</p>
                  <span className={`${INTAKE_MODEL_STATUS_STYLES[m.status]} shrink-0`} data-testid={`intake-model-status-${m.model_id}`}>{INTAKE_MODEL_STATUS_LABELS[m.status]}</span>
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{m.purpose}</p>
              </button>
            ))}
          </div>

          {selected && (
            <div className={`${CT.panel} p-5`} data-testid={`intake-model-detail-${selected.model_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-base font-bold text-slate-900 dark:text-white">{selected.name}</p>
                  {selected.description && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{selected.description}</p>}
                </div>
                <span className={INTAKE_MODEL_STATUS_STYLES[selected.status]}>{INTAKE_MODEL_STATUS_LABELS[selected.status]}</span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <p className={CT.label}>Purpose</p>
                  <p className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">{selected.purpose}</p>
                </div>
                <div>
                  <p className={CT.label}>Destination</p>
                  <p className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">Front Desk</p>
                </div>
                <div>
                  <p className={CT.label}>Fields</p>
                  <p className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">{selected.fields?.length || 0}</p>
                </div>
                <div>
                  <p className={CT.label}>Submissions</p>
                  <p className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-200">{selected.submission_count}</p>
                </div>
              </div>

              <div className="mt-4">
                <p className={CT.label}>Identity Verification</p>
                <div className="mt-1.5 flex items-center gap-3">
                  {(selected.identity_verification?.methods || []).length === 0 ? (
                    <span className="text-sm text-slate-400 dark:text-slate-500">Not configured</span>
                  ) : (
                    selected.identity_verification.methods.map((m) => (
                      <span key={m} className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
                        {m === "email" ? <Mail size={12} /> : <MessageSquare size={12} />} {m === "email" ? "Email OTP" : "SMS OTP"}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-dashed border-slate-200 p-4 dark:border-white/15" data-testid="intake-model-detail-qr-section">
                <p className={CT.label}>QR Codes</p>
                <div className="mt-1.5 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <QrCode size={14} className="text-slate-400 dark:text-slate-500" />
                  {selected.qr_code_count} generated — manage them from the QR Codes tab once this model is active.
                </div>
              </div>

              {canManage && (
                <div className="mt-5 flex items-center gap-2">
                  <button type="button" onClick={() => navigate(`/dashboard/connections/customer-intake/models/${selected.model_id}`)} data-testid={`intake-model-manage-btn-${selected.model_id}`} className={CT.btnPrimary}>
                    {ctaLabel(selected.status)}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDuplicate(selected.model_id)}
                    disabled={busyId === selected.model_id}
                    data-testid={`intake-model-duplicate-btn-${selected.model_id}`}
                    className={CT.btnSecondary}
                  >
                    <Copy size={12} /> Duplicate
                  </button>
                  {selected.status === "draft" ? (
                    <button
                      type="button"
                      onClick={() => handleDelete(selected.model_id, selected.status)}
                      disabled={busyId === selected.model_id}
                      data-testid={`intake-model-delete-btn-${selected.model_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2 text-xs font-bold text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleDelete(selected.model_id, selected.status)}
                      disabled={busyId === selected.model_id}
                      title={selected.qr_code_count > 0 ? "Remove this model's QR Codes first" : "Delete this Intake Model"}
                      data-testid={`intake-model-delete-btn-${selected.model_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-500 hover:border-red-200 hover:bg-red-50 hover:text-red-700 disabled:opacity-50 dark:border-white/15 dark:text-slate-400 dark:hover:border-red-500/30 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
